import React, {ReactNode} from "react";
import {Box, Table, TableBody, TableCell, TableHead, TableRow, Typography, alpha, useTheme} from "@mui/material";
import {Line, LinePeriod, Overview} from "arteco-api-client-ts";
import {Panel} from "../../../../components/kit";
import {eur0, num1, pct1} from "./kpis";

const perHour = (amount?: number, hours?: number) => hours ? amount! / hours : undefined;
const eurH = (n?: number) => n === undefined ? "—" : num1(n) + " €";
const hrs = (n?: number) => n === undefined ? "—" : n.toLocaleString("es-ES", {useGrouping: "always", maximumFractionDigits: 0} as unknown as Intl.NumberFormatOptions) + " h";

interface Row {
	label: string;
	value: (p: LinePeriod) => ReactNode;
	bold?: boolean;
	tone?: (p: LinePeriod) => "good" | "bad" | undefined;
}

/** Las filas de cada tarjeta; {@code months} son los meses del periodo, para el margen por persona y mes. */
const rows = (months: number): Row[] => [
	{label: "Ingresos", value: p => eur0(p.revenue)},
	{label: "Nóminas", value: p => eur0(p.payrollCost)},
	{label: "Margen", value: p => eur0(p.margin), bold: true, tone: p => (p.margin || 0) >= 0 ? "good" : "bad"},
	{label: "Margen %", value: p => pct1(p.marginPct)},
	{label: "Personas", value: p => num1(p.people)},
	{label: "Margen por persona y mes", value: p => p.people ? eur0((p.margin || 0) / (p.people || 1) / months) : "—"},
	{label: "Precio por hora vendida", value: p => eurH(perHour(p.revenue, p.billableHours))},
	{label: "Coste estándar medio por hora", value: p => eurH(p.standardHourCost || undefined)},
	{label: "Capacidad de convenio", value: p => hrs(p.capacityHours)},
	{label: "Horas vendidas (con presupuesto)", value: p => hrs(p.billableHours)},
	{label: "Ocupación", value: p => p.capacityHours ? pct1((p.workedHours || 0) / p.capacityHours * 100) : "—"},
	{label: "Capacidad no vendida", value: p => eur0(p.unsoldCost), bold: true, tone: p => (p.unsoldCost || 0) > 0 ? "bad" : undefined},
];

/** Meses del periodo elegido (1, 3 o 12, o menos si aún no ha terminado). */
const monthsOf = (o: Overview) => {
	const from = new Date(o.current!.from + "T00:00:00");
	const to = new Date(o.current!.to + "T00:00:00");
	return (to.getFullYear() - from.getFullYear()) * 12 + to.getMonth() - from.getMonth() + 1;
};

const Verdict = ({line, referenceRate, months}: { line: Line, referenceRate: number, months: number }) => {
	const theme = useTheme();
	const c = line.current!;
	const price = perHour(c.revenue, c.billableHours);
	const cost = c.standardHourCost || undefined;
	const loss = (c.margin || 0) < 0;
	const color = loss ? theme.palette.error.main : theme.palette.success.main;
	return (
		<Box sx={{border: "1px solid", borderColor: alpha(color, 0.25), bgcolor: alpha(color, 0.08), borderRadius: 2, px: 1.5, py: 1.25, mb: 1.5}}>
			<Typography variant="body2" fontWeight={700} sx={{color}}>
				{loss ? `En pérdidas: ${eur0(c.margin)} en el periodo` : `Margen del ${pct1(c.marginPct)} en el periodo`}
			</Typography>
			<Typography variant="caption" color="text.secondary" component="div" sx={{mt: 0.25}}>
				{loss
					? <>Ingresa {eur0(c.revenue)} y cuesta {eur0(c.payrollCost)}.
						{price !== undefined && cost !== undefined && <> Cada hora vendida entra a {eurH(price)} y cuesta {eurH(cost)}
							{cost > referenceRate ? <>: ni la tarifa de referencia ({eurH(referenceRate)}/h) la cubre.</> : "."}</>}</>
					: <>{c.people ? <>{eur0((c.margin || 0) / (c.people || 1) / months)} por persona y mes. </> : null}
						{price !== undefined && cost !== undefined && <>Cada hora vendida se factura a {eurH(price)} y cuesta {eurH(cost)}. </>}
						Capacidad no vendida: {eur0(c.unsoldCost)}{(c.peopleWithGaps || 0) > 0 && <>, de {c.peopleWithGaps} {c.peopleWithGaps === 1 ? "persona" : "personas"} con huecos</>}.</>}
			</Typography>
		</Box>
	);
};

const cellSx = {fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", px: {xs: 0.5, md: 1}, py: 0.75, fontSize: {xs: 12, md: 13}} as const;

const LineCard = ({line, o}: { line: Line, o: Overview }) => {
	const theme = useTheme();
	const isYear = o.granularity === "YEAR";
	const months = monthsOf(o);
	const cols: { label: string, p?: LinePeriod }[] = [
		{label: o.current!.label!, p: line.current},
		...(isYear ? [] : [{label: o.previous!.label!, p: line.previous}]),
		{label: o.lastYear!.label!, p: line.lastYear},
	];
	return (
		<Box sx={{border: {md: "1px solid"}, borderTop: {xs: "1px solid", md: "1px solid"}, borderColor: {xs: "divider", md: "divider"},
				  borderRadius: {xs: 0, md: 2.5}, p: {xs: 0, md: 2}, pt: {xs: 2, md: 2}, minWidth: 0}}>
			<Typography variant="subtitle2" fontWeight={700}>{line.name}</Typography>
			<Box sx={{mt: 1}}>
				<Verdict line={line} referenceRate={o.settings?.referenceHourRate || 54} months={months}/>
			</Box>
			<Box sx={{overflowX: "auto"}}>
				<Table size="small" sx={{"& td, & th": {borderColor: "rgba(0,0,0,.06)"}}}>
					<TableHead>
						<TableRow>
							<TableCell sx={{...cellSx, pl: 0}}/>
							{cols.map((c, i) => <TableCell key={i} align="right" sx={{...cellSx, fontWeight: 700, color: "text.secondary", bgcolor: "grey.50"}}>{c.label}</TableCell>)}
						</TableRow>
					</TableHead>
					<TableBody>
						{rows(months).map(r => (
							<TableRow key={r.label}>
								<TableCell sx={{...cellSx, pl: 0, whiteSpace: "normal", fontWeight: r.bold ? 700 : 400}}>{r.label}</TableCell>
								{cols.map((c, i) => {
									const tone = c.p && r.tone ? r.tone(c.p) : undefined;
									return (
										<TableCell key={i} align="right" sx={{...cellSx, fontWeight: r.bold ? 700 : 400,
											color: tone === "good" ? theme.palette.success.main : tone === "bad" ? theme.palette.error.main : undefined}}>
											{c.p ? r.value(c.p) : "—"}
										</TableCell>
									);
								})}
							</TableRow>
						))}
						<TableRow>
							<TableCell sx={{...cellSx, pl: 0, whiteSpace: "normal"}}>Cartera aceptada sin facturar (hoy)</TableCell>
							<TableCell align="right" sx={cellSx}>{eur0(line.pendingPortfolio)}</TableCell>
							{cols.slice(1).map((_, i) => <TableCell key={i} align="right" sx={{...cellSx, color: "text.disabled"}}>—</TableCell>)}
						</TableRow>
					</TableBody>
				</Table>
			</Box>
		</Box>
	);
};

export const BusinessLines = ({o}: { o: Overview }) => {
	const lines = o.lines || [];
	const c = o.current!;
	const linesMargin = lines.reduce((acc, l) => acc + (l.current?.margin || 0), 0);
	const structure = o.structure;
	const hours = o.settings?.yearlyHours || 1800;
	return (
		<Panel title="Rentabilidad por línea de negocio"
			   subtitle="Ingresos y nóminas de cada unidad de primer nivel en el mes de su servicio, con sus subunidades dentro. Una tarjeta por línea: si nace otra, sale sola.">
			<Box id="lineas" sx={{display: "grid", gridTemplateColumns: {xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))"}, gap: 2}}>
				{lines.map(l => <LineCard key={l.unitId} line={l} o={o}/>)}
			</Box>
			<Typography variant="caption" color="text.secondary" component="div" sx={{mt: 1.5}}>
				Coste estándar: coste de empresa de las nóminas, anualizado, entre {hours.toLocaleString("es-ES")} h de convenio.
				Capacidad no vendida: persona a persona, sus horas de convenio sin imputar por su coste estándar; quien imputa de más no tapa
				el hueco de otro. La línea de cada persona es la de su unidad a fin de mes.
				{structure && <> Estructura (la unidad raíz, fuera de las líneas): {eur0(structure.payrollCost)} de nóminas
					y {eur0(structure.revenue)} de ingresos. Margen de las líneas, {eur0(linesMargin)}, más estructura = margen bruto, {eur0(c.margin)}.</>}
			</Typography>
		</Panel>
	);
};
